import DeleteDocument from '@Application/Beneficiaries/Documents/Commands/DeleteDocument';
import deleteDocument from '@Application/Beneficiaries/Documents/deleteDocument';
import formatLongDate from '@UI/Helpers/formatLongDate';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import documentToDeleteSignal, {
	closeDocumentToDelete,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/documentToDeleteSignal';
import useDocumentListReturn from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentListReturn';
import useDocumentRoute from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentRoute';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import useFacilityUsers from '@UI/Pages/Beneficiaries/Hooks/useFacilityUsers';
import { ConfirmDeleteDialog } from '@verclam/ui-kit';

/**
 * Hard-deletion confirmation, opened by `documentToDeleteSignal` (the id only); the document is the one
 * the store holds. A verified document names who verified it only when the directory of users is loaded
 * (`USER_READ`), same fallback as the header's "Validé le …" line.
 */
const DeleteDocumentDialog: React.FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { facilityId, subject } = useDocumentRoute();
	const listReturn = useDocumentListReturn(subject);

	const documentToDeleteId = useSignalValue(documentToDeleteSignal);
	const storedDocument = useSelector((state) => state.documentsStore.document);
	const { data: facilityUsers } = useFacilityUsers();

	const document =
		storedDocument !== null && storedDocument.id === documentToDeleteId ? storedDocument : null;

	const message = React.useMemo(() => {
		if (document === null) {
			return '';
		}

		const base = `Le document « ${document.title} » sera définitivement supprimé.`;

		if (document.validatedAt === null) {
			return base;
		}

		const validator = facilityUsers?.findById(document.validatedById) ?? null;
		const datePart = formatLongDate(document.validatedAt);

		return validator !== null
			? `${base} Il a été vérifié par ${validator.fullName} le ${datePart}.`
			: `${base} Il a été vérifié le ${datePart}.`;
	}, [document, facilityUsers]);

	const handleHide = React.useCallback(() => {
		closeDocumentToDelete();
	}, []);

	const handleAccept = React.useCallback(() => {
		if (document === null) {
			return;
		}

		// Leave the detail screen before the success reducer empties its data, which would flash a "not found" state.
		// `acceptCallback` never closes the dialog itself: close it at once too.
		closeDocumentToDelete();
		navigate(listReturn?.listPath ?? buildSubjectPath(subject));
		dispatch(deleteDocument(new DeleteDocument(facilityId, subject, document.id))).catch(
			ignoreHandledError,
		);
	}, [document, dispatch, facilityId, subject, navigate, listReturn]);

	return (
		<ConfirmDeleteDialog
			visible={document !== null}
			onHide={handleHide}
			header="Supprimer définitivement"
			message={message}
			confirmText={document?.title ?? ''}
			acceptCallback={handleAccept}
		/>
	);
};

export default DeleteDocumentDialog;
